import {useState, useContext, useCallback, useRef} from "react"
import styles from "./Dialpad.module.css"
import {Popover} from "antd"
import cx from "classnames"
import {Icon} from "components/Icon"
import {VoiceCallContext} from "context/VoiceCallContext"
import {DialerStudent} from "types/auto-dialer"
import {toastInfo} from "helpers"
import {ReactComponent as CallIcon} from "./assets/call.svg"
import {ReactComponent as RemoveIcon} from "./assets/remove.svg"

type Props = {
    departmentId?: number
}

export function Dialpad({departmentId}: Props) {
    const [visible, setVisible] = useState(false)
    const [phoneNumber, setPhoneNumber] = useState("")
    const {calling, startCall} = useContext(VoiceCallContext)
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null)
    const isLongPressRef = useRef(false)

    const onVisibleChange = (newVisible: boolean) => {
        setVisible(newVisible)
        if (!newVisible) {
            setPhoneNumber("")
            // Clean up long press timer
            if (longPressTimerRef.current) {
                clearTimeout(longPressTimerRef.current)
                longPressTimerRef.current = null
            }
            isLongPressRef.current = false
        }
    }

    const handleNumberClick = (num: string) => {
        setPhoneNumber((prev) => prev + num)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        // Only allow digits, +, *, and #
        const filteredValue = value.replace(/[^0-9+*#]/g, "")
        setPhoneNumber(filteredValue)
    }

    const handleZeroPressStart = () => {
        isLongPressRef.current = false
        const timerId = setTimeout(() => {
            isLongPressRef.current = true
            setPhoneNumber((prev) => prev + "+")
            longPressTimerRef.current = null
        }, 500)
        longPressTimerRef.current = timerId
    }

    const handleZeroPressEnd = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current)
            longPressTimerRef.current = null
            if (!isLongPressRef.current) {
                setPhoneNumber((prev) => prev + "0")
            }
        }
        isLongPressRef.current = false
    }

    const handleBackspace = () => {
        setPhoneNumber((prev) => prev.slice(0, -1))
    }

    const handleCall = useCallback(() => {
        if (!phoneNumber.trim()) {
            toastInfo("Please enter a phone number")
            return
        }

        if (calling) {
            return
        }

        // Format phone number - ensure it starts with + for international format
        let formattedPhone = phoneNumber.trim()
        if (!formattedPhone.startsWith("+")) {
            // If no country code, assume US (+1)
            formattedPhone = `+1${formattedPhone.replace(/\D/g, "")}`
        } else {
            formattedPhone = formattedPhone.replace(/\D/g, "")
            formattedPhone = `+${formattedPhone}`
        }

        const dialerStudent: DialerStudent = {
            phone: formattedPhone,
            fullName: formattedPhone
        }

        startCall(dialerStudent, departmentId)
        setPhoneNumber("")
        setVisible(false)
    }, [phoneNumber, calling, startCall, departmentId])

    const renderDialpad = () => {
        const keypadButtons = [
            {label: "1", value: "1", subLabel: ""},
            {label: "2", value: "2", subLabel: "ABC"},
            {label: "3", value: "3", subLabel: "DEF"},
            {label: "4", value: "4", subLabel: "GHI"},
            {label: "5", value: "5", subLabel: "JKL"},
            {label: "6", value: "6", subLabel: "MNO"},
            {label: "7", value: "7", subLabel: "PQRS"},
            {label: "8", value: "8", subLabel: "TUV"},
            {label: "9", value: "9", subLabel: "WXYZ"},
            {label: "*", value: "*", subLabel: ""},
            {label: "0", value: "0", subLabel: "+"},
            {label: "#", value: "#", subLabel: ""}
        ]

        return (
            <div className={styles.dialpad}>
                <div className={styles.dialpadContent}>
                    <div className={styles.phoneInputWrapper}>
                        <input
                            type="tel"
                            className={styles.phoneInput}
                            value={phoneNumber}
                            onChange={handleInputChange}
                            placeholder="Enter phone number"
                            disabled={calling}
                        />
                    </div>
                    <div className={styles.keypadGrid}>
                        {keypadButtons.map((button) => {
                            if (button.value === "0") {
                                return (
                                    <button
                                        key={button.value}
                                        className={styles.keypadButton}
                                        onMouseDown={handleZeroPressStart}
                                        onMouseUp={handleZeroPressEnd}
                                        onMouseLeave={handleZeroPressEnd}
                                        onTouchStart={handleZeroPressStart}
                                        onTouchEnd={handleZeroPressEnd}
                                        disabled={calling}
                                        type="button">
                                        <span className={styles.keypadButtonLabel}>{button.label}</span>
                                        {button.subLabel && (
                                            <span className={styles.keypadButtonSubLabel}>{button.subLabel}</span>
                                        )}
                                    </button>
                                )
                            }
                            return (
                                <button
                                    key={button.value}
                                    className={styles.keypadButton}
                                    onClick={() => handleNumberClick(button.value)}
                                    disabled={calling}
                                    type="button">
                                    <span className={styles.keypadButtonLabel}>{button.label}</span>
                                    {button.subLabel && (
                                        <span className={styles.keypadButtonSubLabel}>{button.subLabel}</span>
                                    )}
                                </button>
                            )
                        })}
                    </div>
                    <div className={styles.dialpadActions}>
                        <button
                            className={cx(styles.callButton, {
                                [styles.callButton__disabled]: calling || !phoneNumber
                            })}
                            onClick={handleCall}
                            disabled={calling || !phoneNumber}
                            type="button">
                            <CallIcon className={styles.callIcon} />
                        </button>
                        {phoneNumber && (
                            <button
                                className={cx(styles.backspaceButton, {
                                    [styles.backspaceButton__disabled]: calling
                                })}
                                onClick={handleBackspace}
                                disabled={calling}
                                type="button">
                                <RemoveIcon className={styles.backspaceIcon} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Popover
            visible={visible}
            trigger="click"
            placement="bottomRight"
            content={renderDialpad}
            onVisibleChange={onVisibleChange}>
            <div className={cx(styles.dialpadWrap, {[styles.dialpadWrap__opened]: visible})}>
                <Icon icon="DIALPAD" className={styles.dialpadIcon} color={visible ? "#fff" : "#1e90ff"} />
            </div>
        </Popover>
    )
}
